import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Flag, Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const ENVIRONMENTS = ['development', 'testing', 'production'];

export const FeatureFlagsCard = () => {
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [newFlag, setNewFlag] = useState({ name: '', display_name: '', description: '', enabled_environments: ['development'] });

    useEffect(() => {
        api.get('/admin/feature-flags').then(r => setFlags(r.data || [])).catch(e => logger.warn('Feature flags error', e)).finally(() => setLoading(false));
    }, []);

    const handleToggleEnv = async (flagId, env, currentEnvs) => {
        const newEnvs = currentEnvs.includes(env) ? currentEnvs.filter(e => e !== env) : [...currentEnvs, env];
        try {
            await api.put(`/admin/feature-flags/${flagId}`, { enabled_environments: newEnvs });
            setFlags(f => f.map(fl => fl.id === flagId ? { ...fl, enabled_environments: newEnvs } : fl));
            toast.success('Feature flag updated');
        } catch { toast.error('Failed to update feature flag'); }
    };

    const handleCreate = async () => {
        if (!newFlag.name || !newFlag.display_name) { toast.error('Please fill in required fields'); return; }
        try {
            const res = await api.post('/admin/feature-flags', newFlag);
            setFlags([...flags, res.data]);
            setShowDialog(false);
            setNewFlag({ name: '', display_name: '', description: '', enabled_environments: ['development'] });
            toast.success('Feature flag created');
        } catch { toast.error('Failed to create feature flag'); }
    };

    const handleDelete = async (flagId) => {
        if (!confirm('Are you sure you want to delete this feature flag?')) return;
        try {
            await api.delete(`/admin/feature-flags/${flagId}`);
            setFlags(f => f.filter(fl => fl.id !== flagId));
            toast.success('Feature flag deleted');
        } catch { toast.error('Failed to delete feature flag'); }
    };

    if (loading) return null;

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Flag className="h-5 w-5 text-amber-500" />
                            <CardTitle>Feature Flags</CardTitle>
                        </div>
                        <Button onClick={() => setShowDialog(true)} size="sm">
                            <Plus className="h-4 w-4 mr-2" /> Add Flag
                        </Button>
                    </div>
                    <CardDescription>Enable or disable features per environment. Toggle which features are available in Development, Testing, and Production.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-muted rounded-lg text-sm font-medium">
                            <div className="col-span-4">Feature</div>
                            <div className="col-span-2 text-center">Development</div>
                            <div className="col-span-2 text-center">Testing</div>
                            <div className="col-span-2 text-center">Production</div>
                            <div className="col-span-2 text-center">Actions</div>
                        </div>
                        {flags.map(flag => (
                            <div key={flag.id} className="grid grid-cols-12 gap-4 px-4 py-3 border rounded-lg items-center">
                                <div className="col-span-4">
                                    <p className="font-medium">{flag.display_name}</p>
                                    <p className="text-xs text-muted-foreground">{flag.description}</p>
                                    <code className="text-xs bg-muted px-1 rounded">{flag.name}</code>
                                </div>
                                {ENVIRONMENTS.map(env => (
                                    <div key={env} className="col-span-2 flex justify-center">
                                        <Button variant="ghost" size="sm" className={flag.enabled_environments?.includes(env) ? 'text-green-500 hover:text-green-600' : 'text-muted-foreground hover:text-foreground'} onClick={() => handleToggleEnv(flag.id, env, flag.enabled_environments || [])}>
                                            {flag.enabled_environments?.includes(env) ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                                        </Button>
                                    </div>
                                ))}
                                <div className="col-span-2 flex justify-center">
                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(flag.id)}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                        {flags.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">No feature flags configured. Click "Add Flag" to create one.</div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Feature Flag</DialogTitle>
                        <DialogDescription>Add a new feature flag to control feature availability per environment.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Flag Name (code) *</Label>
                            <Input value={newFlag.name} onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value.toLowerCase().replace(/\s/g, '_') })} placeholder="my_feature" />
                        </div>
                        <div className="space-y-2">
                            <Label>Display Name *</Label>
                            <Input value={newFlag.display_name} onChange={(e) => setNewFlag({ ...newFlag, display_name: e.target.value })} placeholder="My Feature" />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input value={newFlag.description} onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })} placeholder="What this feature does..." />
                        </div>
                        <div className="space-y-2">
                            <Label>Enabled Environments</Label>
                            <div className="flex gap-4">
                                {ENVIRONMENTS.map(env => (
                                    <label key={env} className="flex items-center gap-2 cursor-pointer">
                                        <Checkbox checked={newFlag.enabled_environments.includes(env)} onCheckedChange={(checked) => {
                                            const envs = checked ? [...newFlag.enabled_environments, env] : newFlag.enabled_environments.filter(e => e !== env);
                                            setNewFlag({ ...newFlag, enabled_environments: envs });
                                        }} />
                                        <span className="text-sm capitalize">{env}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreate}>Create Flag</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
